import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/ui/page-header';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useAllActivites } from '@/hooks/queries/useActivites';
import {
  useAllSousSegments,
  usePerimetresUser,
  useReplacePerimetres,
} from '@/hooks/queries/usePerimetres';
import { useUsersList } from '@/hooks/queries/useUsersList';
import { cn } from '@/lib/utils';

export function PerimetreUserPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: users = [] } = useUsersList();
  const { data: entreprises = [] } = useEntreprises();
  const { data: activites = [] } = useAllActivites();
  const { data: sousSegments = [] } = useAllSousSegments();
  const { data: existingPerimetres = [] } = usePerimetresUser(id ?? null);
  const replace = useReplacePerimetres();

  const user = users.find((u) => u.user_id === id);

  // selection : Map<key, true> où key = `entrepriseId|activiteId|sousSegmentId|`
  // Une coche sur entreprise = toutes activités, sur activité = tous sous-segments, sur sous-segment = ce seul.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [permConsolide, setPermConsolide] = useState(false);
  const [permModifier, setPermModifier] = useState(true);
  const [permAssigner, setPermAssigner] = useState(false);
  const [dateDebut, setDateDebut] = useState(new Date().toISOString().slice(0, 10));
  const [dateFin, setDateFin] = useState('');
  const [dirty, setDirty] = useState(false);

  // Initialize from existing perimetres
  useEffect(() => {
    const next = new Set<string>();
    let anyConsolide = false,
      anyAssigner = false,
      allModifier = true;
    let firstDebut = '',
      firstFin = '';
    for (const p of existingPerimetres) {
      // Si sous_segment_id null → on coche l'activité (tous sous-segments)
      if (p.sous_segment_id) {
        next.add(`ss:${p.sous_segment_id}`);
      } else {
        next.add(`act:${p.activite_id}`);
      }
      anyConsolide = anyConsolide || p.peut_voir_consolide;
      anyAssigner = anyAssigner || p.peut_assigner_a_autres;
      if (!p.peut_modifier) allModifier = false;
      if (!firstDebut) firstDebut = p.date_debut;
      if (p.date_fin && !firstFin) firstFin = p.date_fin;
    }
    setSelected(next);
    setPermConsolide(anyConsolide);
    setPermAssigner(anyAssigner);
    setPermModifier(existingPerimetres.length === 0 ? true : allModifier);
    if (firstDebut) setDateDebut(firstDebut);
    setDateFin(firstFin);
    setDirty(false);
  }, [existingPerimetres]);

  const toggle = (key: string): void => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setDirty(true);
  };

  const toggleActiviteAll = (activiteId: string, segmentIds: string[]): void => {
    setSelected((cur) => {
      const next = new Set(cur);
      const actKey = `act:${activiteId}`;
      const wasOn = next.has(actKey) || segmentIds.some((sid) => next.has(`ss:${sid}`));
      if (wasOn) {
        next.delete(actKey);
        for (const sid of segmentIds) next.delete(`ss:${sid}`);
      } else {
        // On coche TOUS les sous-segments individuellement (équivalent à "tous")
        // mais on stocke aussi act:id pour signaler "tous"
        next.add(actKey);
      }
      return next;
    });
    setDirty(true);
  };

  const segmentsByActivite = useMemo(() => {
    const m = new Map<string, typeof sousSegments>();
    for (const s of sousSegments) {
      if (!m.has(s.activite_id)) m.set(s.activite_id, []);
      m.get(s.activite_id)!.push(s);
    }
    return m;
  }, [sousSegments]);

  const activitesByEntreprise = useMemo(() => {
    const m = new Map<string, typeof activites>();
    for (const a of activites) {
      if (!m.has(a.entreprise_id)) m.set(a.entreprise_id, []);
      m.get(a.entreprise_id)!.push(a);
    }
    return m;
  }, [activites]);

  const buildPerimetres = (): Parameters<typeof replace.mutateAsync>[0]['perimetres'] => {
    const result: Parameters<typeof replace.mutateAsync>[0]['perimetres'] = [];
    for (const a of activites) {
      const segs = segmentsByActivite.get(a.id) ?? [];
      const actChecked = selected.has(`act:${a.id}`);
      const checkedSegs = segs.filter((s) => selected.has(`ss:${s.id}`));
      if (actChecked) {
        // Tous sous-segments de l'activité (sous_segment_id = null)
        result.push({
          entreprise_id: a.entreprise_id,
          activite_id: a.id,
          sous_segment_id: null,
          peut_voir_consolide: permConsolide,
          peut_modifier: permModifier,
          peut_assigner_a_autres: permAssigner,
          date_debut: dateDebut,
          date_fin: dateFin || null,
        });
      } else if (checkedSegs.length > 0) {
        for (const s of checkedSegs) {
          result.push({
            entreprise_id: a.entreprise_id,
            activite_id: a.id,
            sous_segment_id: s.id,
            peut_voir_consolide: permConsolide,
            peut_modifier: permModifier,
            peut_assigner_a_autres: permAssigner,
            date_debut: dateDebut,
            date_fin: dateFin || null,
          });
        }
      }
    }
    return result;
  };

  const handleSave = async (): Promise<void> => {
    if (!id) return;
    const perimetres = buildPerimetres();
    if (perimetres.length === 0 && user?.role !== 'super_admin' && user?.role !== 'direction') {
      toast.error('Sélectionnez au moins une activité ou un sous-segment.');
      return;
    }
    try {
      await replace.mutateAsync({ userId: id, perimetres });
      toast.success(`Périmètre enregistré (${perimetres.length} ligne(s))`);
      setDirty(false);
    } catch (e) {
      toast.error(`Erreur : ${(e as Error).message}`);
    }
  };

  if (!user) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        <Link
          to="/parametres/utilisateurs"
          className="inline-flex items-center gap-1 hover:underline"
        >
          <ChevronLeft className="h-3 w-3" /> Retour
        </Link>
        <p className="mt-3">Utilisateur introuvable.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <Link
        to="/parametres/utilisateurs"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-3 w-3" /> Retour à la liste utilisateurs
      </Link>
      <PageHeader
        title={`Périmètre — ${user.full_name ?? user.user_id.slice(0, 8)}`}
        subtitle={
          <span className="text-xs text-muted-foreground">
            <Badge variant="outline" className="mr-2">
              {user.role}
            </Badge>
            Cochez les activités / sous-segments accessibles. Coche au niveau activité = tous les
            sous-segments.
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/parametres/utilisateurs')}>
              Annuler
            </Button>
            <Button onClick={() => void handleSave()} disabled={!dirty || replace.isPending}>
              {replace.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Enregistrer
            </Button>
          </div>
        }
      />

      <div className="rounded-lg border border-border bg-card p-4">
        <div className="space-y-4">
          {entreprises.map((ent) => {
            const ents = activitesByEntreprise.get(ent.id) ?? [];
            return (
              <div key={ent.id} className="rounded-md border border-border/50 p-3">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: ent.couleur_accent ?? '#6366f1' }}
                  />
                  {ent.nom}
                </div>
                {ents.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Aucune activité.</p>
                ) : (
                  <ul className="space-y-2 pl-2">
                    {ents.map((act) => {
                      const segs = segmentsByActivite.get(act.id) ?? [];
                      const actKey = `act:${act.id}`;
                      const actChecked = selected.has(actKey);
                      const someSegChecked = segs.some((s) => selected.has(`ss:${s.id}`));
                      return (
                        <li key={act.id} className="space-y-1">
                          <label className="flex items-center gap-2 text-xs">
                            <input
                              type="checkbox"
                              checked={actChecked}
                              ref={(el) => {
                                if (el) el.indeterminate = !actChecked && someSegChecked;
                              }}
                              onChange={() =>
                                toggleActiviteAll(
                                  act.id,
                                  segs.map((s) => s.id),
                                )
                              }
                            />
                            <span className="font-medium">{act.nom}</span>
                            {actChecked && (
                              <Badge variant="success" className="text-[10px]">
                                Tous sous-segments
                              </Badge>
                            )}
                          </label>
                          {segs.length > 0 && !actChecked && (
                            <ul className="ml-6 space-y-1">
                              {segs.map((s) => (
                                <li key={s.id}>
                                  <label className="flex items-center gap-2 text-[11px]">
                                    <input
                                      type="checkbox"
                                      checked={selected.has(`ss:${s.id}`)}
                                      onChange={() => toggle(`ss:${s.id}`)}
                                    />
                                    {s.nom}
                                  </label>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold">Permissions spéciales</h3>
        <div className="space-y-2 text-xs">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={permConsolide}
              onChange={(e) => {
                setPermConsolide(e.target.checked);
                setDirty(true);
              }}
            />
            Peut voir les KPIs consolidés (toutes activités du périmètre fusionnées)
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={permAssigner}
              onChange={(e) => {
                setPermAssigner(e.target.checked);
                setDirty(true);
              }}
            />
            Peut assigner / réassigner des opportunités à d&apos;autres commerciaux
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={permModifier}
              onChange={(e) => {
                setPermModifier(e.target.checked);
                setDirty(true);
              }}
            />
            Peut modifier (sinon lecture seule sur le périmètre)
          </label>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold">Période d&apos;affectation</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Date de début</span>
            <input
              type="date"
              value={dateDebut}
              onChange={(e) => {
                setDateDebut(e.target.value);
                setDirty(true);
              }}
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
            />
          </label>
          <label className="space-y-1 text-xs">
            <span className="text-muted-foreground">Date de fin (optionnel)</span>
            <input
              type="date"
              value={dateFin}
              onChange={(e) => {
                setDateFin(e.target.value);
                setDirty(true);
              }}
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              placeholder="Indéfinie"
            />
          </label>
        </div>
      </div>

      <div className={cn('text-xs', dirty ? 'text-warning' : 'text-muted-foreground')}>
        {dirty
          ? 'Modifications non enregistrées'
          : `${existingPerimetres.length} périmètre(s) actuellement attribué(s)`}
      </div>
    </div>
  );
}
